/**
 * Auto-capitalisation for a text field, as a pure function (2026-10-08).
 *
 * WHY THIS EXISTS. The platform already has `autocapitalize`, and on a phone
 * it is the right tool — but it is a hint to the VIRTUAL keyboard. MDN and the
 * HTML spec (§6.8.7) both say it "does not affect behavior when typing on a
 * physical keyboard", and desktop Safari ignores it outright. The admin app is
 * used at a desk, so sentence capitalisation there has to happen in JS. This
 * is that JS. `BdrInput` turns the native hint off while this is on, so ONE
 * system owns the field on every device and the refusal below works the same
 * way everywhere.
 *
 * `Intl.Segmenter` with `granularity: "sentence"` is not an alternative. UAX
 * #29 rule SB8 refuses to break before a lowercase letter — "hello. t" is one
 * sentence, "hello. T" is two — so it decides the boundary FROM the case of
 * the letter that follows, which is the thing we are trying to decide.
 *
 * THE CONTRACT. `autoCapSentences(prev, next, state)` → `{ value, state }`.
 * `prev` is what the field held before the edit, `next` is what the DOM holds
 * now, `state` is whatever this returned last time (start from
 * `AUTO_CAP_INITIAL_STATE`, and go back to it when the field is replaced from
 * outside — a send, a retry). No refs, no React, no DOM: the caller owns the
 * state, which is what makes this testable.
 *
 * ONLY TYPED TEXT IS CAPITALISED. The edit is diffed against `prev` and only
 * characters inside the inserted span are candidates. That is the whole fix
 * for the SMS composer's worst bug — "Https://x.com", delete the H, get
 * "Ttps://x.com", one character eaten per attempt — which came from
 * re-scanning the whole string on every change, so a deletion capitalised
 * whatever it exposed. A deletion inserts nothing, so here it can never
 * capitalise anything. Pasting and select-all-replace are just big inserts.
 *
 * A candidate is capitalised when it starts the message (everything before it
 * is whitespace or opening punctuation) or starts a sentence (preceded by
 * whitespace, optional closing punctuation, then `.` `!` or `?`), EXCEPT:
 *   - inside a URL or e-mail address — `https://`, `www.`, a bare domain on
 *     the same TLD allow-list as `linkify.js`, or `user@host`;
 *   - after an ellipsis (`...` or `…`);
 *   - after an abbreviation: a dotted token (`e.g.`, `p.m.`, `U.S.`), a lone
 *     initial (`J.`), or one of `AUTO_CAP_ABBREVIATIONS` (`Mr.`, `Blvd.`);
 *   - after a decimal point — there is no whitespace after the dot in
 *     `$12.50`, so the whitespace rule already covers it.
 * A newline is a sentence start (iOS does the same after Return). A character
 * whose uppercase has a different length (`ß` → `SS`) is left alone.
 *
 * THE REFUSAL (owner-approved; carried over from the SMS composer). If WE
 * capitalised a letter and the user deletes exactly that letter and types the
 * same letter again, that is "I meant lowercase": it stays lowercase, and when
 * it was the first letter of the message the first letter is left alone until
 * the field is emptied. A letter the user shifted themselves was never ours,
 * so deleting it arms nothing. The old composer could only honour this for a
 * one-character field; the diff lets it work at any position, in any length
 * of message, and as a single select-and-retype too.
 *
 * SCHEME REVERT. Typing a URL one key at a time still produces "Https://" at
 * the moment the `h` goes in — nothing can know yet. So when a capital OF OURS
 * turns out to be inside `https://`, `www.` or `user@host`, it is put back to
 * the lowercase the user typed. Bare domains are deliberately not reverted:
 * "Amazon.com is down" may be exactly what was meant.
 */

/** Dotted words that end a word, not a sentence. Lowercase, no dot. */
export const AUTO_CAP_ABBREVIATIONS = new Set([
  "mr", "mrs", "ms", "dr", "prof", "sr", "jr", "st", "vs", "etc", "approx",
  "est", "appt", "ave", "blvd", "rd", "ln", "ct", "hwy", "apt", "ste", "dept",
]);

export const AUTO_CAP_INITIAL_STATE = Object.freeze({
  /** `{ index, original }` for every capital that is OURS, in the current value. */
  capped: Object.freeze([]),
  /** `{ index, letter }` after our capital was deleted: retyping `letter` there is the refusal. */
  armed: null,
  /** The message-start capital was refused; leave that position alone until the field empties. */
  honourLowercase: false,
});

// Keep the TLD list in lockstep with linkify.js (and the phone's linkify.dart).
const TLDS = "com|net|org|us|co|io|app|dev|edu|gov|info";
const TOKEN_PATTERN = new RegExp(
  "(?:https?:\\/\\/[^\\s<>\"]+" +
    "|www\\.[^\\s<>\"]+" +
    "|[a-z0-9._%+-]+@[a-z0-9-]+(?:\\.[a-z0-9-]+)*\\.[a-z]{2,}" +
    `|\\b[a-z0-9-]+(?:\\.[a-z0-9-]+)*\\.(?:${TLDS})\\b(?:\\/[^\\s<>"]*)?)`,
  "gi"
);
// Sentence punctuation glued to the end of a link belongs to the sentence.
const TRAILING = new Set([".", ",", ";", ":", "!", "?", ")", "]", "}", "'", '"']);
// Shapes where a capital is never wanted, so one of ours is safe to undo.
const REVERT_SHAPE = /^(?:https?:\/\/|www\.|[^\s@]+@)/i;

const OPENING = new Set(['"', "'", "(", "[", "{", "“", "‘", "¿", "¡"]);
const CLOSING = new Set(['"', "'", ")", "]", "}", "”", "’"]);
const isSpace = (ch) => /\s/.test(ch);

/** URL / e-mail spans in `text`, as `{ start, end, text }` with trailing punctuation shed. */
const tokenSpans = (text) => {
  const spans = [];
  TOKEN_PATTERN.lastIndex = 0;
  let m;
  while ((m = TOKEN_PATTERN.exec(text)) !== null) {
    let end = m.index + m[0].length;
    while (end > m.index && TRAILING.has(text[end - 1])) end -= 1;
    if (end > m.index) spans.push({ start: m.index, end, text: text.slice(m.index, end) });
  }
  return spans;
};

const spanAt = (spans, i) => spans.find((s) => s.start <= i && i < s.end) || null;

/** The one edit that turns `prev` into `next`: common prefix / suffix, then the middle. */
const diffEdit = (prev, next) => {
  const max = Math.min(prev.length, next.length);
  let p = 0;
  while (p < max && prev[p] === next[p]) p += 1;
  let s = 0;
  while (s < max - p && prev[prev.length - 1 - s] === next[next.length - 1 - s]) s += 1;
  return {
    start: p,
    removed: prev.slice(p, prev.length - s),
    inserted: next.slice(p, next.length - s),
  };
};

/** The whole code point at `i` — never half a surrogate pair. */
const charAt = (text, i) => {
  const cp = text.codePointAt(i);
  return cp === undefined ? "" : String.fromCodePoint(cp);
};

/** A lowercase letter whose capital is the same width — `ß` → `SS` is refused. */
const cappable = (ch) => {
  const up = ch.toUpperCase();
  return up !== ch && up.length === ch.length;
};

/**
 * "message" when everything before `i` is whitespace / opening punctuation,
 * "sentence" when `i` follows a terminator (or a newline), else null.
 */
const startKind = (text, i, spans) => {
  let j = 0;
  while (j < i && (isSpace(text[j]) || OPENING.has(text[j]))) j += 1;
  if (j === i) return "message";

  j = i - 1;
  let sawSpace = false;
  while (j >= 0 && isSpace(text[j])) {
    if (text[j] === "\n" || text[j] === "\r") return "sentence";
    sawSpace = true;
    j -= 1;
  }
  if (!sawSpace) return null;
  while (j >= 0 && CLOSING.has(text[j])) j -= 1;
  if (j < 0) return null;

  const t = text[j];
  if (t === "!" || t === "?") return "sentence";
  if (t !== ".") return null;
  if (j > 0 && text[j - 1] === ".") return null; // ellipsis
  // "x.com." ends a sentence even though the token has dots in it.
  if (spans.some((s) => s.end === j)) return "sentence";

  let k = j - 1;
  while (k >= 0 && !isSpace(text[k])) k -= 1;
  const word = text.slice(k + 1, j).replace(/^[\s"'([{“‘¿¡]+/, "");
  if (word.includes(".")) return null; // e.g. / p.m. / U.S.
  if (/^\p{L}$/u.test(word)) return null; // an initial: J.
  if (AUTO_CAP_ABBREVIATIONS.has(word.toLowerCase())) return null;
  return "sentence";
};

const setChar = (text, i, ch) => text.slice(0, i) + ch + text.slice(i + ch.length);

/**
 * @param {string} prevValue  the field's value before this edit
 * @param {string} nextValue  the field's value after it (what the DOM holds)
 * @param {object} state      the state returned last time; `AUTO_CAP_INITIAL_STATE` to begin
 * @returns {{ value: string, state: object }}  the corrected value and the state to keep
 */
export const autoCapSentences = (prevValue, nextValue, state = AUTO_CAP_INITIAL_STATE) => {
  const prev = prevValue == null ? "" : String(prevValue);
  const next = nextValue == null ? "" : String(nextValue);
  const st = state || AUTO_CAP_INITIAL_STATE;
  if (prev === next) return { value: next, state: st };

  const { start, removed, inserted } = diffEdit(prev, next);
  const removedEnd = start + removed.length;
  const delta = inserted.length - removed.length;

  // Carry our capitals across the edit: before it they stay, inside it they
  // are gone, after it they shift.
  const capped = [];
  st.capped.forEach((c) => {
    if (c.index < start) capped.push(c);
    else if (c.index >= removedEnd) capped.push({ index: c.index + delta, original: c.original });
  });

  // The refusal. Arm when exactly one of our capitals was removed; fire when
  // its original letter is typed back into the same place — in the next edit,
  // or in this one (select the capital, type the letter).
  const ours = removed.length === 1 ? st.capped.find((c) => c.index === start) : undefined;
  const armedNow = ours ? { index: start, letter: ours.original } : null;
  const retyped = (a) => !!a && start === a.index && inserted === a.letter;
  const refusedAt =
    retyped(armedNow) || (removed.length === 0 && retyped(st.armed)) ? start : -1;
  let armed = inserted.length === 0 ? armedNow : null;
  let honourLowercase = st.honourLowercase;

  if (next.length === 0) {
    return { value: next, state: { capped: [], armed, honourLowercase: false } };
  }

  const spans = tokenSpans(next);
  let value = next;

  if (refusedAt >= 0 && startKind(next, refusedAt, spans) === "message") {
    honourLowercase = true;
  }

  for (let i = start; i < start + inserted.length; ) {
    const ch = charAt(next, i);
    const width = ch.length || 1;
    if (i !== refusedAt && cappable(ch) && !spanAt(spans, i)) {
      const kind = startKind(next, i, spans);
      if (kind === "sentence" || (kind === "message" && !honourLowercase)) {
        value = setChar(value, i, ch.toUpperCase());
        capped.push({ index: i, original: ch });
      }
    }
    i += width;
  }

  // Scheme revert: a capital of ours that now sits inside https:// / www. /
  // user@host goes back to what was typed.
  const kept = [];
  capped.forEach((c) => {
    const span = spanAt(spans, c.index);
    if (span && REVERT_SHAPE.test(span.text)) {
      value = setChar(value, c.index, c.original);
    } else {
      kept.push(c);
    }
  });
  kept.sort((a, b) => a.index - b.index);

  return { value, state: { capped: kept, armed, honourLowercase } };
};

export default autoCapSentences;
